'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import type { Message } from '../data/mockMessages';
import { useInboxTheme } from '@/contexts/InboxThemeContext';

interface MessageListProps {
  messages: Message[];
  selectedId: string | null;
  onSelect: (m: Message) => void;
  folder: 'inbox' | 'starred' | 'trash';
}

function formatTime(iso: string) {
  const date = new Date(iso);
  const now = new Date('2026-09-01T23:41:53Z');
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

const folderLabels: Record<string, string> = {
  inbox: 'Inbox',
  starred: 'Starred',
  trash: 'Trash',
};

export default function MessageList({ messages, selectedId, onSelect, folder }: MessageListProps) {
  const { themeConfig } = useInboxTheme();

  if (messages.length === 0) {
    return (
      <div className="w-80 flex-shrink-0 inbox-glass-sidebar border-r border-white/10 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 rounded-xl inbox-glass border border-white/15 flex items-center justify-center mb-4">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="inbox-text-muted">
            <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
            <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
          </svg>
        </div>
        <h3 className="text-sm font-semibold inbox-text-foreground mb-1">
          No messages in {folderLabels[folder]}
        </h3>
        <p className="text-xs inbox-text-muted leading-relaxed">
          Messages sent to your temp address will appear here in real time.
        </p>
      </div>
    );
  }

  return (
    <div className="w-80 flex-shrink-0 inbox-glass-sidebar border-r border-white/10 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between flex-shrink-0">
        <h2 className="text-sm font-semibold inbox-text-foreground">
          {folderLabels[folder]}
          <span className="ml-2 text-xs font-normal inbox-text-muted">
            {messages.length} message{messages.length !== 1 ? 's' : ''}
          </span>
        </h2>
        <div className="flex items-center gap-1">
          <button
            className="p-1.5 rounded-lg inbox-text-muted hover:inbox-text-foreground hover:bg-white/10 transition-all duration-150"
            title="Sort messages"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {messages.map((msg) => (
          <button
            key={`msg-item-${msg.id}`}
            onClick={() => onSelect(msg)}
            className={`w-full text-left px-4 py-3.5 border-b border-white/8 transition-all duration-150 hover:bg-white/8 ${
              selectedId === msg.id ? 'bg-white/12 border-l-2' : ''
            }`}
            style={selectedId === msg.id ? { borderLeftColor: themeConfig.color } : {}}
          >
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mt-0.5 border border-white/15">
                <AppImage
                  src={msg.avatarUrl}
                  alt={msg.avatarAlt}
                  width={32}
                  height={32}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className={`text-sm truncate ${!msg.isRead ? 'font-semibold inbox-text-foreground' : 'font-medium inbox-text-secondary'}`}>
                    {msg.from}
                  </span>
                  <span className="text-xs inbox-text-muted flex-shrink-0 tabular-nums">
                    {formatTime(msg.receivedAt)}
                  </span>
                </div>
                <p className={`text-xs truncate mb-1 ${!msg.isRead ? 'font-medium inbox-text-foreground' : 'inbox-text-secondary'}`}>
                  {msg.subject}
                </p>
                <p className="text-xs inbox-text-muted truncate leading-relaxed">
                  {msg.preview}
                </p>

                {/* Indicators */}
                <div className="flex items-center gap-1.5 mt-1.5">
                  {!msg.isRead && (
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: themeConfig.color }} />
                  )}
                  {msg.starred && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="#FFB830" stroke="#FFB830" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  )}
                  {msg.attachments.length > 0 && (
                    <span className="flex items-center gap-0.5 inbox-text-muted">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                      </svg>
                      <span className="text-xs">{msg.attachments.length}</span>
                    </span>
                  )}
                  <span className="ml-auto text-xs inbox-text-muted">{msg.size}</span>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}